// Admin > General > "DMR / Analog codeplug programming": the network's codeplug profiles
// (documentation/protocol.md "DMR codeplug profiles"). A profile is only a template: a radio gets
// it by an explicit load in Admin > Radios > Edit > Radio-Specific Settings > DMR settings, and
// later profile changes never alter radios that were loaded before.
import { useEffect, useRef, useState } from 'react';
import {
  CodeplugApiError,
  createCodeplugProfile,
  deleteCodeplugProfile,
  getCodeplugProfile,
  listCodeplugProfiles,
  updateCodeplugProfile,
  type Codeplug,
  type CodeplugProfileSummary,
} from '../../services/dmrCodeplug';
import CodeplugEditor from './CodeplugEditor';
import { checkWorkbookSize, codeplugExportFilename, codeplugToXlsx, downloadXlsx, xlsxToCodeplug, WorkbookError } from './codeplugXlsx';
import { LIMITS, codeplugProblems, emptyCodeplug, profileProblems } from './dmrCodeplugModel';

interface Draft {
  id: string | null; // null = new profile
  revision: number;
  name: string;
  description: string;
  codeplug: Codeplug;
}

const btn = 'px-3 py-2 rounded text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed';

export function ProfileList({
  profiles,
  onEdit,
  onExport,
  onDelete,
  busy,
}: {
  profiles: CodeplugProfileSummary[];
  onEdit: (id: string) => void;
  onExport: (id: string) => void;
  onDelete: (p: CodeplugProfileSummary) => void;
  busy?: boolean;
}) {
  if (profiles.length === 0) {
    return <p className="text-sm text-gray-400" data-no-profiles>No codeplug profiles in this network yet.</p>;
  }
  return (
    <table className="w-full text-sm" data-profile-list>
      <thead>
        <tr className="text-left text-xs text-gray-400">
          <th className="py-1">Name</th><th>Zones</th><th>Channels</th><th>Radios loaded from it</th><th>Revision</th><th>Updated</th><th />
        </tr>
      </thead>
      <tbody>
        {profiles.map((p) => (
          <tr key={p.id} className="border-t border-gray-700">
            <td className="py-2 pr-2">
              <p className="font-semibold text-white">{p.name}</p>
              {p.description && <p className="text-xs text-gray-400">{p.description}</p>}
            </td>
            <td>{p.zone_count}</td>
            <td>{p.channel_count}</td>
            <td>{p.radio_count}</td>
            <td>{p.revision}</td>
            <td className="text-xs text-gray-400">{new Date(p.updated_at).toLocaleString()}</td>
            <td className="text-right space-x-1 whitespace-nowrap">
              <button type="button" className={`${btn} bg-blue-700 hover:bg-blue-600 text-white`} disabled={busy} onClick={() => onEdit(p.id)}>Edit</button>
              <button type="button" className={`${btn} bg-gray-700 hover:bg-gray-600 text-white`} disabled={busy} onClick={() => onExport(p.id)}>⬇ .xlsx</button>
              <button type="button" className={`${btn} bg-red-800 hover:bg-red-700 text-white`} disabled={busy} onClick={() => onDelete(p)}>Delete</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * The section belongs to one network. Switching the network remounts it, so no list, draft,
 * message or late response of the previous network is shown or saved into the new one (React
 * drops updates of the unmounted view), and every request uses the network it was opened for.
 */
export default function DmrCodeplugProfilesManager({ orgId }: { orgId: string }) {
  return <ProfilesManagerView key={orgId} orgId={orgId} />;
}

export function ProfilesManagerView({ orgId }: { orgId: string }) {
  const [profiles, setProfiles] = useState<CodeplugProfileSummary[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [conflict, setConflict] = useState<number | null>(null);
  const newImport = useRef<HTMLInputElement>(null);
  const draftImport = useRef<HTMLInputElement>(null);
  const latest = useRef(0);

  // Only the most recently started list request may set the list (an older one can finish later).
  const load = async () => {
    const seq = ++latest.current;
    if (!orgId) {
      setProfiles([]);
      return;
    }
    try {
      const list = await listCodeplugProfiles(orgId);
      if (seq !== latest.current) return;
      setProfiles(list);
      setError(null);
    } catch (e) {
      if (seq === latest.current) setError(e instanceof Error ? e.message : 'Failed to load codeplug profiles');
    }
  };

  useEffect(() => {
    load();
  }, [orgId]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await fn();
    } catch (e) {
      if (e instanceof CodeplugApiError && e.code === 'REVISION_CONFLICT') setConflict(e.currentRevision ?? null);
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  const edit = (id: string) =>
    run(async () => {
      const p = await getCodeplugProfile(orgId, id);
      setConflict(null);
      setDraft({ id: p.id, revision: p.revision, name: p.name, description: p.description, codeplug: p.codeplug });
    });

  const exportProfile = (id: string) =>
    run(async () => {
      const p = await getCodeplugProfile(orgId, id);
      downloadXlsx(codeplugToXlsx({ name: p.name, description: p.description, codeplug: p.codeplug }), codeplugExportFilename(p.name));
    });

  const remove = (p: CodeplugProfileSummary) => {
    const radios = p.radio_count > 0 ? `\n\n${p.radio_count} radio(s) were loaded from it; they keep their own copy.` : '';
    if (!confirm(`Delete codeplug profile "${p.name}"?${radios}`)) return;
    run(async () => {
      try {
        await deleteCodeplugProfile(orgId, p.id, p.revision);
      } catch (e) {
        // Saved by someone else since the list was loaded: show the current list, delete nothing.
        if (e instanceof CodeplugApiError && e.code === 'REVISION_CONFLICT') {
          await load();
          throw new Error(`"${p.name}" was changed by someone else (now revision ${e.currentRevision}); it was not deleted. Review it and try again.`);
        }
        throw e;
      }
      await load();
      setMessage('Profile deleted.');
    });
  };

  const readFile = async (file: File) => {
    try {
      checkWorkbookSize(file.size); // before the file is read into memory
      return xlsxToCodeplug(await file.arrayBuffer());
    } catch (e) {
      setError(e instanceof WorkbookError ? e.message : 'Failed to read the workbook.');
      return null;
    }
  };

  const importAsNew = async (file: File) => {
    setError(null);
    const parsed = await readFile(file);
    if (!parsed) return;
    setConflict(null);
    setDraft({ id: null, revision: 0, name: parsed.name || file.name.replace(/\.xlsx$/i, ''), description: parsed.description, codeplug: parsed.codeplug });
    setMessage('Workbook imported into a new, unsaved profile. Review it and Save.');
  };

  const importIntoDraft = async (file: File) => {
    if (!draft) return;
    setError(null);
    const parsed = await readFile(file);
    if (!parsed) return;
    if (!confirm('Replace the zones, channels, contacts and RX group lists in the editor with the workbook? Nothing is saved until you press Save.')) return;
    setDraft({ ...draft, codeplug: parsed.codeplug });
    setMessage('Workbook imported into the editor (not saved yet).');
  };

  const save = () =>
    draft &&
    run(async () => {
      const saved = draft.id
        ? await updateCodeplugProfile(orgId, draft.id, draft.revision, draft.name, draft.description, draft.codeplug)
        : await createCodeplugProfile(orgId, draft.name, draft.description, draft.codeplug);
      setDraft({ id: saved.id, revision: saved.revision, name: saved.name, description: saved.description, codeplug: saved.codeplug });
      setConflict(null);
      await load();
      setMessage(`Profile saved (revision ${saved.revision}). Radios already loaded from it are not changed.`);
    });

  const problems = draft ? [...profileProblems(draft.name, draft.description), ...codeplugProblems(draft.codeplug)] : [];

  return (
    <section className="bg-gray-800 border border-gray-700 rounded-lg p-6 space-y-5" data-codeplug-profiles>
      <div>
        <h2 className="text-2xl font-bold text-white">DMR / Analog codeplug programming</h2>
        <p className="text-sm text-gray-400 mt-1">
          Codeplug profiles of this network for radios with a DMR module (IRC380): zones, digital and analog channels,
          contacts and RX group lists (up to {LIMITS.zones} zones, {LIMITS.channelsPerZone} channels per zone,{' '}
          {LIMITS.channels} channels). A profile is loaded into a radio explicitly in Radios &gt; Edit &gt; Radio-Specific
          Settings &gt; DMR settings; changing a profile here never changes radios that were already loaded. Encryption,
          scan lists, time-out timers and remote kill/stun are not managed here.
        </p>
      </div>

      {error && <p className="rounded bg-red-900/40 border border-red-700 px-3 py-2 text-sm text-red-200" role="alert">{error}</p>}
      {message && <p className="rounded bg-green-900/30 border border-green-700 px-3 py-2 text-sm text-green-200">{message}</p>}

      {!draft && (
        <>
          <div className="flex flex-wrap gap-2">
            <button type="button" className={`${btn} bg-green-700 hover:bg-green-600 text-white`} disabled={busy || !orgId}
              onClick={() => { setConflict(null); setMessage(null); setDraft({ id: null, revision: 0, name: '', description: '', codeplug: emptyCodeplug() }); }}>
              + New profile
            </button>
            <button type="button" className={`${btn} bg-gray-700 hover:bg-gray-600 text-white`} disabled={busy || !orgId} onClick={() => newImport.current?.click()}>
              ⬆ Import .xlsx as new profile
            </button>
            <input ref={newImport} type="file" className="hidden" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) importAsNew(f); }} />
          </div>
          <ProfileList profiles={profiles} busy={busy} onEdit={edit} onExport={exportProfile} onDelete={remove} />
        </>
      )}

      {draft && (
        <div className="space-y-4" data-profile-editor>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="block">
              <span className="text-sm font-semibold text-gray-300">Profile name</span>
              <input className="mt-1 w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white" value={draft.name} maxLength={LIMITS.profileName}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </label>
            <label className="block">
              <span className="text-sm font-semibold text-gray-300">Description</span>
              <input className="mt-1 w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white" value={draft.description} maxLength={LIMITS.profileDescription}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
            </label>
          </div>
          <p className="text-xs text-gray-500">{draft.id ? `Editing revision ${draft.revision}.` : 'New profile (not saved yet).'}</p>

          <CodeplugEditor value={draft.codeplug} onChange={(codeplug) => setDraft({ ...draft, codeplug })} />

          {conflict !== null && draft.id && (
            <p className="rounded border border-amber-700 bg-amber-900/20 px-3 py-2 text-sm text-amber-200">
              Someone else saved this profile (now revision {conflict}). Your changes were not saved.{' '}
              <button type="button" className="underline" onClick={() => draft.id && edit(draft.id)}>Reload the latest version</button> (discards your changes) or
              export your version to .xlsx first.
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <button type="button" className={`${btn} bg-blue-600 hover:bg-blue-500 text-white`} disabled={busy || problems.length > 0} onClick={save}>
              {draft.id ? 'Save profile' : 'Create profile'}
            </button>
            <button type="button" className={`${btn} bg-gray-700 hover:bg-gray-600 text-white`} disabled={busy} onClick={() => { setDraft(null); setConflict(null); setMessage(null); load(); }}>
              Close
            </button>
            <button type="button" className={`${btn} bg-gray-700 hover:bg-gray-600 text-white`} disabled={busy}
              onClick={() => downloadXlsx(codeplugToXlsx({ name: draft.name, description: draft.description, codeplug: draft.codeplug }), codeplugExportFilename(draft.name))}>
              ⬇ Export editor to .xlsx
            </button>
            <button type="button" className={`${btn} bg-gray-700 hover:bg-gray-600 text-white`} disabled={busy} onClick={() => draftImport.current?.click()}>
              ⬆ Import .xlsx into editor
            </button>
            <input ref={draftImport} type="file" className="hidden" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) importIntoDraft(f); }} />
          </div>
          {profileProblems(draft.name, draft.description).map((p) => <p key={p} className="text-xs text-amber-300">{p}</p>)}
        </div>
      )}
    </section>
  );
}
